import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { StyledTable } from '@app/components/table/Table';
import { ActionsDropdown } from '@app/components/actions-dropdown/ActionsDropdown';
import { UserDeleteModal } from './components/UserDeleteModal';
import { UserEditModal } from './components/UserEditModal';
import { UserAddModal } from './components/UserAddModal';
import { PageMeta } from '@app/components/meta/Meta';
import { PageHeader } from '@app/components/page/PageHeader';
import { PageTitle } from '@app/components/page/PageTitle';
import { PageLayout } from '@app/components/page';
import { UserAddWidget } from './components/UserAddWidget';
import { UserListFilterWidget } from './components/UserListFilterWidget';
import type { TableColumnsType } from 'antd';
import type { User } from '@app/services/api.types';
import useModal from '@app/hooks/useModal';
import useUserList from '@app/hooks/users/useUsers';
import { fn } from '@app/utils';
import { url } from '@app/utils/constants';

export const UserList = () => {
    const navigate = useNavigate();
    const [params, setParams] = useState({
        page: 1,
        limit: 10,
        skip: 0,
    });
    const modalAdd = useModal<User>({
        initialData: undefined,
    });
    const modalEdit = useModal<User>({
        initialData: undefined,
    });
    const modalDelete = useModal<User>({
        initialData: undefined,
    });

    // Fetch data from the API
    const { isLoading, data: userList } = useUserList(params);

    const actionMenuItems = (record: User) => [
        {
            key: '1',
            label: 'View User',
            onClick: () => navigate(url.adminPortal.users.view(record.id)),
        },
        {
            key: '2',
            label: 'Edit User',
            onClick: () => openEditModal(record),
        },
        {
            key: '3',
            label: 'Delete User',
            onClick: () => openDeleteModal(record),
        },
    ];

    const columns: TableColumnsType<User> = [
        {
            title: 'ID',
            dataIndex: 'id',
            render: (value) => value || '-',
            sorter: true,
            width: 100,
        },
        {
            title: 'Name',
            dataIndex: 'firstName',
            key: 'firstName',
            sorter: true,
            render: (_text, record) => {
                const fullName = `${record.first_name || ''} ${record.last_name || ''}`;
                return <span>{fullName.trim()}</span>;
            },
        },
        {
            title: 'Email',
            dataIndex: 'email',
            key: 'email',
            sorter: true,
        },
        {
            title: 'Role',
            dataIndex: 'role',
            key: 'role',
            sorter: true,
            render: (role) => {
                return roles?.find((item) => item.id === role)?.name || 'Unknown Role';
            },
        },
        {
            key: 'actions',
            width: 130,
            fixed: 'right',
            render: (_text, record) => (
                <ActionsDropdown title="Actions" items={actionMenuItems(record)} data={record} />
            ),
        },
    ];

    const openAddModal = () => {
        modalAdd.open();
    };

    const openEditModal = (record: User) => {
        modalEdit.open(record);
    };

    const openDeleteModal = (record: User) => {
        modalDelete.open(record);
    };

    const closeAddModal = () => {
        modalAdd.reset();
    };

    const closeEditModal = () => {
        modalEdit.reset();
    };

    const closeDeleteModal = () => {
        modalDelete.reset();
        resetFilters();
    };

    const resetFilters = () => {
        setParams((prev) => ({ ...prev, page: 1, skip: 0 }));
    };

    const roles = [
        {
            id: 'admin',
            name: 'Admin',
        },
        {
            id: 'moderator',
            name: 'Moderator',
        },
        {
            id: 'user',
            name: 'User',
        },
    ];

    return (
        <React.Fragment>
            <PageMeta title="Users List" />

            <PageHeader
                breadcrumbItems={[
                    { title: <Link to={url.adminPortal.dashboard}>Dashboard</Link> },
                    { title: 'Users' },
                ]}>
                <PageTitle title="User List" />
            </PageHeader>

            <PageLayout>
                <PageLayout.Sidebar>
                    <UserListFilterWidget
                        onSubmit={() => console.log('Apply Filters')}
                        onReset={resetFilters}
                    />
                    <UserAddWidget onAdd={openAddModal} />
                </PageLayout.Sidebar>

                <PageLayout.Content>
                    <StyledTable<User>
                        columns={columns}
                        dataSource={userList?.data || []}
                        rowKey={({ id }) => id}
                        // scroll={{ x: 860 }}
                        loading={isLoading}
                        pagination={{
                            current: params.page,
                            pageSize: params.limit,
                            total: userList?.total || 0,
                        }}
                        onChange={(pagination, _filters, sorter) => {
                            const singleSorter = Array.isArray(sorter) ? sorter[0] : sorter;
                            const order_by = singleSorter?.field as string | undefined;
                            const order = fn.getTableOrderDir(
                                order_by,
                                singleSorter?.order ?? undefined
                            );
                            setParams((prev) => ({
                                ...prev,
                                page: pagination.current || 1,
                                skip: ((pagination.current || 1) - 1) * (pagination.pageSize || 10),
                                sortBy: order_by,
                                order,
                            }));
                        }}
                    />
                </PageLayout.Content>
            </PageLayout>

            <UserAddModal
                open={modalAdd.isOpen}
                data={modalAdd.data}
                roles={roles}
                onCancel={closeAddModal}
                onOk={closeAddModal}
            />

            <UserEditModal
                open={modalEdit.isOpen}
                data={modalEdit.data}
                roles={roles}
                onCancel={closeEditModal}
                onOk={closeEditModal}
            />

            <UserDeleteModal
                open={modalDelete.isOpen}
                data={modalDelete.data}
                onCancel={closeDeleteModal}
                onOk={closeDeleteModal}
            />
        </React.Fragment>
    );
};

export default UserList;
